// A member's day: date picker, route on the map and a stops/trips timeline.
import type { Schemas } from "@10twenty/api";
import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { api, ApiError, errorMessage, unwrap } from "../api";
import { useAuth } from "../auth/AuthContext";
import { checkInTitle } from "../checkins/checkIns";
import {
  dayKey,
  dayRange,
  dayTitle,
  distanceText,
  durationText,
  resolveDay,
  shiftDay,
  speedText,
  timeline,
  timeText,
  totalDistance,
  tripAt,
  type TimelineEntry,
} from "../history/history";
import { statsPath } from "../stats/stats";
import type { Units } from "../units";
import { useUnits } from "../useUnits";
import { useNow } from "../useNow";

type HistoryResult = { history: Schemas["History"] } | { denied: ApiError };

const HistoryMap = lazy(() => import("../map/HistoryMap"));

/** Today's history is reloaded this often while shown (as on Android). */
const REFRESH_MS = 60_000;
/** Re-evaluates which day is "today". */
const TICK_MS = 60_000;

export function HistoryPage() {
  const { circleId = "", userId = "" } = useParams();
  const [search, setSearch] = useSearchParams();
  const { session } = useAuth();
  const nowMs = useNow(TICK_MS);
  const today = dayKey(new Date(nowMs));
  const day = resolveDay(search.get("day"), nowMs);
  // The route and timeline shown: selection and the camera fit belong to one of these.
  const view = `${circleId}/${userId}/${day}`;
  // `key: null` is a cleared selection; until the first choice a `?trip=` (from the weekly summary) selects that drive.
  const [selection, setSelection] = useState<{ view: string; key: string | null } | null>(null);
  const units = useUnits();
  const panelRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLOListElement>(null);

  const members = useQuery({
    queryKey: ["circle", circleId, "members"],
    queryFn: async () => unwrap(await api.GET("/circles/{circleId}/members", { params: { path: { circleId } } })),
  });
  const member = members.data?.find((m) => m.user_id === userId);
  const name = member ? (member.user_id === session?.userId ? `${member.name} (you)` : member.name) : "Member";

  const history = useQuery({
    queryKey: ["circle", circleId, "history", userId, day],
    queryFn: async ({ signal }): Promise<HistoryResult> => {
      try {
        const result = await api.GET("/circles/{circleId}/members/{userId}/history", {
          params: { path: { circleId, userId }, query: dayRange(day) },
          signal,
        });
        return { history: unwrap(result) };
      } catch (error) {
        // Losing access replaces the cached route at once, and a later network
        // failure cannot bring it back; other failures keep it.
        if (error instanceof ApiError && (error.status === 403 || error.status === 404)) return { denied: error };
        throw error;
      }
    },
    refetchInterval: day === today ? REFRESH_MS : false,
  });
  const denied = history.data && "denied" in history.data ? history.data.denied : null;
  const data = history.data && "history" in history.data ? history.data.history : undefined;
  const paused = data?.sharing === "paused";
  const points = useMemo(() => (data && !paused ? data.points : []), [data, paused]);
  const entries = useMemo(
    () => (data && !paused ? timeline(data.segments, data.place_events, data.check_ins) : []),
    [data, paused],
  );
  // Kept by kind and start time, so a refresh that merges segments cannot move it to another entry.
  const tripParam = Number(search.get("trip"));
  const linkedTrip = Number.isFinite(tripParam) && tripParam > 0 ? tripAt(entries, tripParam) : null;
  const selectedKey = selection?.view === view ? selection.key : linkedTrip;
  const selected = entries.some((e) => e.key === selectedKey) ? selectedKey : null;

  const goTo = (next: string) => setSearch(next === today ? {} : { day: next }, { replace: true });

  const select = (key: string) => setSelection({ view, key: key === selected ? null : key });

  // An entry selected on the map scrolls into view.
  useEffect(() => {
    if (selected === null) return;
    listRef.current?.querySelector(`[data-key="${CSS.escape(selected)}"]`)?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  const stops = entries.filter((e) => e.kind === "stop").length;
  const distance = data && !paused ? totalDistance(data.segments) : 0;

  return (
    <div className="history-page">
      <Suspense fallback={<div className="live-map" />}>
        <HistoryMap
          points={points}
          entries={entries}
          selectedKey={selected}
          fitKey={view}
          onSelect={select}
          overlayRef={panelRef}
        />
      </Suspense>
      <aside ref={panelRef} className="history-panel" aria-label={`${name}'s history`}>
        <div className="history-header">
          <Link to="/" className="muted">
            ← Map
          </Link>
          <h1>{name}</h1>
          <Link to={statsPath(circleId, userId)} className="history-driving-link">
            Driving
          </Link>
        </div>
        <div className="history-day-bar">
          <button type="button" className="secondary" onClick={() => goTo(shiftDay(day, -1))} aria-label="Earlier day">
            ‹
          </button>
          <label className="history-day">
            <span>{dayTitle(day, nowMs)}</span>
            <input
              type="date"
              value={day}
              max={today}
              required
              onChange={(event) => {
                if (event.target.value) goTo(resolveDay(event.target.value, Date.now()));
              }}
            />
          </label>
          <button
            type="button"
            className="secondary"
            onClick={() => goTo(shiftDay(day, 1))}
            disabled={day >= today}
            aria-label="Later day"
          >
            ›
          </button>
        </div>
        <div className="history-body" role="status">
          {denied ? (
            <p className="error">
              {denied.status === 404 ? "This member isn't in this circle, or you aren't." : errorMessage(denied)}
            </p>
          ) : history.isPending ? (
            <p className="muted">Loading history…</p>
          ) : !data ? (
            <p className="error">{errorMessage(history.error)}</p>
          ) : paused ? (
            <p className="muted">{name} has paused location sharing.</p>
          ) : (
            <>
              {history.error && <p className="error">{errorMessage(history.error)}</p>}
              {entries.length === 0 ? (
                <p className="muted">{points.length === 0 ? "No location data for this day." : "No stops or trips."}</p>
              ) : (
                <p className="muted">
                  {stops} {stops === 1 ? "stop" : "stops"} · {distanceText(distance, units)} travelled
                </p>
              )}
            </>
          )}
        </div>
        {entries.length > 0 && (
          <ol className="history-timeline" ref={listRef}>
            {entries.map((entry) => (
              <TimelineItem
                key={entry.key}
                entry={entry}
                units={units}
                selected={entry.key === selected}
                onSelect={select}
              />
            ))}
          </ol>
        )}
      </aside>
    </div>
  );
}

function TimelineItem({
  entry,
  units,
  selected,
  onSelect,
}: {
  entry: TimelineEntry;
  units: Units;
  selected: boolean;
  onSelect: (key: string) => void;
}) {
  if (entry.kind === "check-in") {
    return (
      <li className="history-entry check-in" data-key={entry.key}>
        <span className="history-entry-title">{checkInTitle(entry.checkIn)}</span>
        <span className="history-entry-meta">{timeText(entry.start)}</span>
      </li>
    );
  }
  if (entry.kind === "place") {
    const arrived = entry.event.kind === "arrived";
    return (
      <li className={`history-entry place ${entry.event.kind}`} data-key={entry.key}>
        <span className="history-entry-title">
          {arrived ? "Arrived at" : "Left"} {entry.event.place_name ?? "a place"}
        </span>
        <span className="history-entry-meta">{timeText(entry.start)}</span>
      </li>
    );
  }
  const times = `${timeText(entry.start)} – ${timeText(entry.end)}`;
  const duration = durationText(entry.end - entry.start);
  if (entry.kind === "no-data") {
    return (
      <li className="history-entry no-data" data-key={entry.key}>
        <span className="history-entry-title">No location data</span>
        <span className="history-entry-meta">
          {times} · {duration}
        </span>
      </li>
    );
  }
  const title =
    entry.kind === "stop"
      ? "Stop"
      : `${entry.trip.mode === "drive" ? "Drive" : "Walk"} · ${distanceText(entry.trip.distance_m, units)}${
          entry.trip.max_speed_mps === undefined ? "" : ` · up to ${speedText(entry.trip.max_speed_mps, units)}`
        }`;
  return (
    <li className={`history-entry ${entry.kind}${selected ? " selected" : ""}`} data-key={entry.key}>
      <button type="button" onClick={() => onSelect(entry.key)} aria-pressed={selected}>
        <span className="history-entry-title">{title}</span>
        <span className="history-entry-meta">
          {times} · {duration}
        </span>
      </button>
    </li>
  );
}
